/* ══════════════════════════════════════════════════════════════════
   ww-order-hardware.js  -  2026-09-22 (rev 3: falls back to the cart's Year/Make/Model attributes when the Convermax API isn't ready)
   Saves the selected vehicle's hardware specs (from Convermax
   WheelTireVehicleInfo) as cart attributes so they land on the order
   in "Additional details", right under Year/Make/Model, and flow into
   the order notification / PO emails through the `attributes` loop.

   Also strips Convermax's combined "Vehicle" attribute, which only
   duplicates Year/Make/Model/Submodel/Option.

   Loaded from sections/cart-template.liquid (all checkouts go through
   the cart page: cart_action = redirect_cart). Standalone - does not
   depend on fitment-ww.js.
   ══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────
  // Set to false to keep Convermax's combined "Vehicle" attribute.
  var STRIP_VEHICLE_ATTR = true;

  // Order attribute name  →  candidate keys in Convermax WheelData.
  // Keys are matched case-insensitively, ignoring spaces/underscores.
  var FIELDS = [
    ['Hardware',     ['Hardware', 'HardwareType']],
    ['Thread',       ['Thread', 'ThreadSize']],
    ['Hex',          ['Hex', 'HexSize']],
    ['TPMS',         ['TPMS']],
    ['TPMS Type',    ['TPMSType']],
    ['Seat Type',    ['SeatType']],
    ['Bolt Pattern', ['BoltPattern']],
    ['Hub Bore',     ['HubBore', 'CenterBore']]
  ];

  var CACHE_KEY = 'wwOrderHardware.v2';
  var cfg       = window.wwOrderHardware || {};
  var current   = cfg.current || {};   // attribute values already on the cart (from Liquid)
  var running   = false;
  var rerun     = false;
  var settled   = false;   // true once at least one sync has finished on this page
  var waiters   = [];

  function log() {
    if (!window.console || !console.debug) return;
    var args = ['[ww-order-hardware]'].concat([].slice.call(arguments));
    console.debug.apply(console, args);
  }

  // ── Convermax helpers ────────────────────────────────────────────
  // The vehicle from Convermax's API, else the Year/Make/Model attributes Convermax has already
  // written on the cart (rendered into cfg.vehicle by cart-template.liquid). The second source means
  // the specs still get saved when Convermax's script is slow or fails to load on the cart page.
  function getVehicle() {
    try {
      var v = (window.Convermax && typeof window.Convermax.getVehicle === 'function')
        ? (window.Convermax.getVehicle() || {})
        : {};
      if (v && v.Year && v.Make && v.Model) return v;
    } catch (e) {}
    var a = cfg.vehicle || {};
    return (a.Year && a.Make && a.Model) ? a : {};
  }

  function signature(v) {
    if (!v || !v.Year || !v.Make || !v.Model) return '';
    return [v.Year, v.Make, v.Model, v.Submodel || '', v.Option || ''].join('|');
  }

  // Same slug rules fitment-ww.js uses for facet selections
  function encodeVehicleParam(val) {
    var t = document.createElement('textarea');
    t.innerHTML = val || '';
    return t.value.replace(/[\s\/\']+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  }

  // Vehicle-only request - identical to the add-ons engine's
  // buildConvermaxUrlFromVehicle() (proven to return WheelTireVehicleInfo
  // with no product in the cart).
  function buildVehicleUrl(vehicle) {
    var params = new URLSearchParams();
    params.set('pagesize', '1');
    var idx = 0;
    params.set('facet.' + idx + '.field', 'collection_ids');
    params.set('facet.' + idx + '.selection', '263239729218');
    idx++;
    ['Year', 'Make', 'Model', 'Submodel', 'Option'].forEach(function (f) {
      if (!vehicle[f]) return;
      params.set('facet.' + idx + '.field', f);
      params.set('facet.' + idx + '.selection', String(vehicle[f]));
      idx++;
    });
    params.set('extra.country', 'us');
    params.set('extra.currency', 'cad');
    params.set('extra.currencyrate', '1.0');
    params.set('extra.locale', 'en');
    params.set('extra.pagetype', 'category');
    return 'https://wheelwiz.myconvermax.com/search.json?' + params.toString();
  }

  // Fallback: fitment-ww.js style VerifyFitment request against a wheel in the cart.
  function buildVerifyUrl(vehicle) {
    var parts = ['filterquery=id%3A' + cfg.productId, 'pagesize=1', 'mode=VerifyFitment'];
    var fi = 0;
    ['Year', 'Make', 'Model', 'Submodel', 'Option'].forEach(function (f) {
      if (!vehicle[f]) return;
      var sel = f === 'Year' ? String(vehicle[f]) : encodeVehicleParam(String(vehicle[f]));
      parts.push('facet.' + fi + '.field=' + f);
      parts.push('facet.' + fi + '.selection=' + encodeURIComponent(sel));
      fi++;
    });
    parts.push('extra.country=ca', 'extra.currency=cad', 'extra.currencyrate=1.0', 'extra.locale=en', 'extra.pagetype=product');
    if (cfg.variantId) parts.push('extra.variantid=' + cfg.variantId);
    parts.push('variantsMode=Strip');
    return 'https://wheelwiz.myconvermax.com/search.json?' + parts.join('&');
  }

  function norm(k) { return String(k).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  function pick(sources, candidates) {
    for (var s = 0; s < sources.length; s++) {
      var src = sources[s];
      if (!src || typeof src !== 'object') continue;
      var lookup = {};
      Object.keys(src).forEach(function (k) { lookup[norm(k)] = src[k]; });
      for (var c = 0; c < candidates.length; c++) {
        var val = lookup[norm(candidates[c])];
        if (val === undefined || val === null || typeof val === 'object') continue;
        if (val === true)  return 'Yes';
        if (val === false) return 'No';
        val = String(val).trim();
        if (val !== '') return val;
      }
    }
    return '';
  }

  function extract(data) {
    var info  = data && data.Extra && data.Extra.WheelTireVehicleInfo;
    if (!info) return null;
    var wheel = info.WheelData || null;
    var out = {}, found = 0;
    FIELDS.forEach(function (f) {
      // Same precedence as the add-ons engine: FrontAxle → RearAxle → WheelData
      out[f[0]] = pick([wheel && wheel.FrontAxle, wheel && wheel.RearAxle, wheel, info], f[1]);
      if (out[f[0]]) found++;
    });
    return found ? out : null;
  }

  async function fetchSpecs(vehicle) {
    var urls = [buildVehicleUrl(vehicle)];
    if (cfg.productId) urls.push(buildVerifyUrl(vehicle));
    for (var i = 0; i < urls.length; i++) {
      try {
        var res  = await fetch(urls[i]);
        var data = await res.json();
        var specs = extract(data);
        if (specs) return specs;
        log('no WheelTireVehicleInfo in response', i === 0 ? '(vehicle search)' : '(VerifyFitment)');
      } catch (e) {
        log('Convermax request failed', e);
      }
    }
    return null;
  }

  // ── Cache (avoids re-hitting Convermax on every cart reload) ─────
  function readCache(sig) {
    try {
      var c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      return (c && c.sig === sig && c.specs) ? c.specs : null;
    } catch (e) { return null; }
  }
  function writeCache(sig, specs) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ sig: sig, specs: specs })); } catch (e) {}
  }

  function blankSpecs() {
    var out = {};
    FIELDS.forEach(function (f) { out[f[0]] = ''; });
    return out;
  }

  // ── Cart write ───────────────────────────────────────────────────
  async function pushAttributes(desired) {
    var diff = {}, changed = false;
    Object.keys(desired).forEach(function (k) {
      if ((current[k] || '') !== (desired[k] || '')) { diff[k] = desired[k] || ''; changed = true; }
    });
    if (!changed) { log('cart attributes already up to date'); return; }

    log('updating cart attributes', diff);
    var res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: diff })
    });
    if (!res.ok) { log('cart update failed', res.status); return; }
    var cart = await res.json();
    current = (cart && cart.attributes) || current;
  }

  async function sync() {
    if (running) { rerun = true; return; }
    running = true;
    try {
      var vehicle = getVehicle();
      var sig     = signature(vehicle);
      var desired;

      if (!sig) {
        desired = blankSpecs();                 // no vehicle → no hardware specs
      } else {
        desired = readCache(sig);
        if (!desired) {
          desired = await fetchSpecs(vehicle);
          if (desired) writeCache(sig, desired);
          else desired = blankSpecs();          // never keep another vehicle's specs
        }
      }

      if (STRIP_VEHICLE_ATTR) desired.Vehicle = '';
      await pushAttributes(desired);
    } catch (e) {
      log('sync error', e);
    } finally {
      running = false;
      if (rerun) { rerun = false; sync(); }
      else { settled = true; waiters.splice(0).forEach(function (fn) { fn(); }); }
    }
  }

  // Resolves as soon as no sync is in flight (or after `ms` at most)
  function whenSettled(ms) {
    return new Promise(function (resolve) {
      if (settled && !running) return resolve();
      var t = setTimeout(resolve, ms);
      waiters.push(function () { clearTimeout(t); resolve(); });
    });
  }

  // ── Checkout gate ────────────────────────────────────────────────
  // The attributes are written with a background call; if the shopper hits Checkout before that call
  // has answered, the order is created without them. Hold the submit for up to 4s until it has.
  var releasing = false;
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.id !== 'cart_form' || releasing) return;
    if (settled && !running) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var submitter = e.submitter || form.querySelector('[name="checkout"]');
    log('holding checkout until cart attributes are saved');
    whenSettled(4000).then(function () {
      releasing = true;
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else form.submit();
      } finally { setTimeout(function () { releasing = false; }, 0); }
    });
  }, true);

  // ── "Vehicle" duplicate: also blank it in the form POST ──────────
  // Covers the case where Convermax re-writes the attribute after our
  // cart/update call, or injects its own hidden input into the form.
  function stripVehicleFromForm() {
    if (!STRIP_VEHICLE_ATTR) return;
    var form = document.getElementById('cart_form');
    if (!form) return;
    form.querySelectorAll('input[name="attributes[Vehicle]"]').forEach(function (el) {
      if (el.id !== 'ww-strip-vehicle-attr') el.value = '';
    });
    var own = document.getElementById('ww-strip-vehicle-attr');
    if (!own) {
      own = document.createElement('input');
      own.type = 'hidden';
      own.id   = 'ww-strip-vehicle-attr';
      own.name = 'attributes[Vehicle]';
      own.value = '';
    }
    form.appendChild(own);   // keep it last so it wins over any duplicate
  }

  // ── Wiring ───────────────────────────────────────────────────────
  function chain(name) {
    window.Convermax = window.Convermax || {};
    var prev = window.Convermax[name];
    window.Convermax[name] = function () {
      try { if (typeof prev === 'function') prev.apply(this, arguments); } catch (e) {}
      setTimeout(sync, 0);
    };
  }

  function start() {
    // wait (max ~6s) for the Convermax script to expose getVehicle()
    // Run right away from the cart's own Year/Make/Model attributes, then again once Convermax is up
    // (its API may know a newer vehicle). Waits up to ~20s for Convermax instead of giving up at 6s.
    sync();
    var tries = 0;
    (function wait() {
      if (window.Convermax && typeof window.Convermax.getVehicle === 'function') {
        chain('onVehicleSelected');
        chain('onVehicleRemoved');
        sync();
      } else if (tries++ < 100) {
        setTimeout(wait, 200);
      } else {
        log('Convermax API never became available; used the cart attributes only');
      }
    })();

    stripVehicleFromForm();
    document.addEventListener('submit', function (e) {
      if (e.target && e.target.id === 'cart_form') stripVehicleFromForm();
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[name="checkout"]')) stripVehicleFromForm();
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();